/**
 * "Perfect phrase" counter state machine (PPC).
 *
 * Tracks whether the user plays a looped section with 100% accuracy across
 * consecutive iterations and maintains a streak of "perfect points".
 *
 * Lifecycle (driven by the scene's loop controller via PerfectPhraseController):
 *
 *   loopStarted()  → LOOP_STARTED      → enter `started` (or stay `continuing`)
 *   loopTick(ok)   → ACCURACY_DROPPED  → `stopped` (streak reset) when ok=false
 *   loopCompleted()→ LOOP_COMPLETED    → award + `continuing` when still perfect,
 *                                        else `stopped` (streak reset)
 *   reset()        → RESET             → `idle` (streak reset) from any state
 *
 * State graph:
 *
 *   idle ──LOOP_STARTED──▶ started
 *
 *   started ──ACCURACY_DROPPED──▶ stopped            (resetStreak)
 *   started ──LOOP_COMPLETED[perfect]──▶ continuing   (awardPoint)
 *   started ──LOOP_COMPLETED[!perfect]──▶ stopped      (resetStreak)
 *
 *   continuing ──LOOP_STARTED──▶ continuing           (streak carries)
 *   continuing ──ACCURACY_DROPPED──▶ stopped          (resetStreak)
 *   continuing ──LOOP_COMPLETED[perfect]──▶ continuing (awardPoint)
 *   continuing ──LOOP_COMPLETED[!perfect]──▶ stopped   (resetStreak)
 *
 *   stopped ──LOOP_STARTED──▶ started                 (fresh attempt; streak 0)
 *
 *   * ──RESET──▶ idle                                 (resetStreak)
 *
 * The machine is intentionally pure — the only observable outputs are
 * `context.streak` / `context.perfectPoints`. The scene subscribes and renders
 * the streak badge from context; no imperative side effects live here.
 */

import { setup, assign } from 'xstate';

import type { PerfectPhraseContext, PerfectPhraseEvent } from './perfectPhraseMachine.types';

const initialContext: PerfectPhraseContext = {
  streak: 0,
  perfectPoints: 0,
};

export const perfectPhraseMachine = setup({
  types: {
    context: {} as PerfectPhraseContext,
    events: {} as PerfectPhraseEvent,
  },
  guards: {
    finishedPerfect: ({ event }) => event.type === 'LOOP_COMPLETED' && event.finalPerfect,
  },
  actions: {
    /** A perfect loop completed: bump streak + lifetime points. */
    awardPoint: assign({
      streak: ({ context }) => context.streak + 1,
      perfectPoints: ({ context }) => context.perfectPoints + 1,
    }),
    /** A streak-breaking event occurred: zero the current streak. */
    resetStreak: assign({ streak: () => 0 }),
  },
}).createMachine({
  id: 'perfectPhrase',
  initial: 'idle',
  context: initialContext,
  on: {
    // A hard reset can arrive in any state: loop-area change, tempo change,
    // long pause (>60s), or looping disabled entirely.
    RESET: { target: '.idle', actions: 'resetStreak' },
  },
  states: {
    idle: {
      on: {
        LOOP_STARTED: 'started',
      },
    },

    started: {
      on: {
        ACCURACY_DROPPED: { target: 'stopped', actions: 'resetStreak' },
        LOOP_COMPLETED: [
          { target: 'continuing', guard: 'finishedPerfect', actions: 'awardPoint' },
          { target: 'stopped', actions: 'resetStreak' },
        ],
        // A new iteration starting without a completion in between (rare):
        // re-enter started fresh.
        LOOP_STARTED: { target: 'started', reenter: true },
      },
    },

    continuing: {
      on: {
        // Next iteration begins; the streak carries over.
        LOOP_STARTED: 'continuing',
        ACCURACY_DROPPED: { target: 'stopped', actions: 'resetStreak' },
        LOOP_COMPLETED: [
          { target: 'continuing', guard: 'finishedPerfect', actions: 'awardPoint' },
          { target: 'stopped', actions: 'resetStreak' },
        ],
      },
    },

    stopped: {
      on: {
        // The streak is already 0; a new iteration starts a fresh attempt.
        LOOP_STARTED: 'started',
      },
    },
  },
});

export type PerfectPhraseMachine = typeof perfectPhraseMachine;
